import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Save, Check } from "lucide-react";

/**
 * TESM — Tender Early Screening Matrix (user view).
 * Mirrors the design tokens used by the TRCA tab (card-flat, stat-label,
 * brand colour, divide-y) but hides ALL scoring from the user. The questionnaire
 * answers are still posted to the backend, which computes and stores the score.
 */
export default function TesmModule({ tenderId, schema, scoreDoc, onSaved }) {
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (scoreDoc) {
      setAnswers(scoreDoc.answers || {});
      setNotes(scoreDoc.notes || "");
    } else {
      setAnswers({});
      setNotes("");
    }
  }, [scoreDoc, tenderId]);

  const answered = useMemo(() => {
    if (!schema) return 0;
    const valid = new Set([0, 4, 6, 8, 10]);
    let n = 0;
    schema.questions.forEach((q) => {
      const v = answers[q.key];
      if (Number.isFinite(v) && valid.has(Number(v))) n += 1;
    });
    return n;
  }, [answers, schema]);

  if (!schema) return null;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/tenders/${tenderId}/tesm`, { answers, notes });
      toast.success("TESM responses saved");
      onSaved?.();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-up" data-testid="module-tesm">
      {/* Left — questions */}
      <div className="lg:col-span-2 card-flat p-6">
        <div className="flex items-end justify-between mb-2 flex-wrap gap-2">
          <div>
            <div className="stat-label">TESM</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mt-1">
              TESM — Tender Early Screening Matrix
            </h2>
            <p className="text-sm text-ink-muted mt-1">Choose one answer per question.</p>
          </div>
        </div>

        <div className="mt-6 divide-y divide-ink-line">
          {schema.questions.map((q, idx) => {
            const selected = answers[q.key];
            return (
              <div key={q.key} className="py-4" data-testid={`tesm-question-${q.key}`}>
                <div className="mb-3">
                  <div className="text-sm font-medium">
                    <span className="font-num text-ink-muted mr-2">{String(idx + 1).padStart(2, "0")}.</span>
                    {q.label}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {q.options.map((o) => {
                    const isSel = selected === o.score;
                    return (
                      <button
                        type="button"
                        key={`${q.key}-${o.score}-${o.label}`}
                        onClick={() => setAnswers({ ...answers, [q.key]: o.score })}
                        data-testid={`tesm-${q.key}-opt-${o.score}`}
                        className={`flex items-center gap-2 text-left px-3 py-2 text-sm rounded-sm border transition-colors ${
                          isSel
                            ? "border-brand bg-brand/5 text-ink"
                            : "border-ink-line bg-white hover:bg-secondary text-ink-muted hover:text-ink"
                        }`}
                      >
                        <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                          isSel ? "border-brand bg-brand text-white" : "border-ink-line"
                        }`}>
                          {isSel && <Check className="w-3 h-3" />}
                        </span>
                        <span>{o.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6">
          <label className="stat-label block mb-1.5">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            data-testid="tesm-notes"
            className="w-full px-3 py-2 border border-ink-line rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
          />
        </div>

        <div className="mt-5 flex justify-end">
          <button
            onClick={submit}
            disabled={saving}
            data-testid="tesm-save-btn"
            className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-1.5 transition-colors disabled:opacity-60"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Right — Progress + computed score (per fix_tesm_and_bidscore_not_showing.md §1.3) */}
      <div className="card-flat p-6 flex flex-col">
        <div className="stat-label">Progress</div>
        <div className="font-num text-5xl font-bold mt-2 leading-none text-brand" data-testid="tesm-answered">
          {answered}<span className="text-ink-muted">/{schema.questions.length}</span>
        </div>
        <div className="text-[11px] uppercase tracking-wider text-ink-muted mt-2">Questions answered</div>

        {/* Score block — shows once TESM has been saved at least once */}
        {scoreDoc && Number.isFinite(scoreDoc.score) ? (
          <div className="mt-6 pt-6 border-t border-ink-line" data-testid="tesm-score-block">
            <div className="stat-label">TESM Score</div>
            <div className="font-num font-bold text-4xl mt-1.5" data-testid="tesm-score-value">
              {scoreDoc.score}<span className="text-ink-muted text-lg font-normal"> / 100</span>
            </div>
            {scoreDoc.band && (
              <div className="mt-3" data-testid="tesm-score-band">
                {(() => {
                  const tone = scoreDoc.band_tone;
                  const cls = tone === "good" ? "bg-good-soft text-good border border-good/40"
                    : tone === "warn" ? "bg-warn-soft text-warn border border-warn/40"
                    : tone === "muted" ? "bg-secondary text-ink border border-ink-line"
                    : tone === "ink" ? "bg-red-50 text-red-700 border border-red-300"
                    : "bg-brand-soft text-brand border border-brand/40";
                  return <span className={`chip text-[11px] font-display font-bold tracking-tight ${cls}`}>{scoreDoc.band}</span>;
                })()}
                {scoreDoc.band_desc && (
                  <div className="text-[11px] text-ink-muted mt-2 leading-snug">{scoreDoc.band_desc}</div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-6 pt-6 border-t border-ink-line text-[12px] text-ink-muted leading-relaxed">
            Pick the option that best describes the tender for each question, then save to compute your TESM score.
          </div>
        )}
      </div>
    </div>
  );
}
